// Workforce › Agents › one agent (`/c/{slug}/agents/ag/{id}`; WF-FIX, Andrei 2026-10-26: "you
// can't open an individual agent"). The agent (AG) on its own page: a location header (the AG
// badge and name, its status; for those who may change it, Pause / Resume, Take over, Reassign,
// Edit and Retire as labelled buttons — `ag-actions.tsx`, each saying it changes the CompanyOS
// record and not the Hermes container), then (WF-POLISH) what it does, whom it reports to (a DE
// links to its profile; a person has no profile page yet, so a Message button opens the DM),
// which DE uses it, its recent work, and the runtime behind an (i). One call on load (the
// agent), plus the Reassign / Edit choices for someone who may change it.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  InfoTip,
  LocationHeader,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { MessageSquareIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { WorkforceAgentRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { NotFoundPage } from "../../pages/status-pages";
import type { CompanyAccess } from "../company";
import { RuntimeSyncChip } from "../control/runtime-sync-chip";
import {
  Expandable,
  ProfileDialog,
  type ProfileDraft,
  ProfileFields,
  RuntimeLine,
} from "../de/de-about";
import { problemText } from "../de/de-status";
import { formatDate, formatDateTime } from "../format";
import { myMembershipId } from "../home/home-queries";
import { keys, workforceAgentQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { useFeature, useSession } from "../use-feature";
import { AG_RECORD_HINT, AG_STATUS, AgActions, activityLabel, useAgChoices } from "./ag-actions";
import { useOpenDm } from "./agents-ux";
import { SkillChips } from "./helpers";

export function AgDetailScreen({
  api,
  company,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const agent = useQuery(workforceAgentQuery(api, company.id, id));
  const a = agent.data;
  const choices = useAgChoices(api, company.id, a?.can_edit === true);
  if (agent.isError && agent.error instanceof ApiError && agent.error.status === 404)
    return <NotFoundPage message="No such agent in this company." />;
  const name = a ? workerName(a.name) : "Agent";
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="ag-detail" data-ag={id}>
      <LocationHeader
        crumbs={[
          {
            label: "Agents",
            render: (
              <Link to="/c/$slug/agents/ag" params={{ slug: company.slug }} search={keepFocus} />
            ),
          },
        ]}
        title={
          <span className="inline-flex min-w-0 items-center gap-2">
            <span className="truncate">{name}</span>
            <WorkerBadge kind="ag" />
          </span>
        }
        badge={
          a ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {a.team ? (
                <Badge variant="outline" className="max-sm:hidden">
                  {a.team.name}
                </Badge>
              ) : null}
              <Badge variant={AG_STATUS[a.status].variant} data-slot="ag-status">
                {AG_STATUS[a.status].label}
              </Badge>
            </span>
          ) : null
        }
        actions={
          a ? (
            <AgActions api={api} company={company} agent={a} choices={choices} variant="page" />
          ) : null
        }
      />
      <div className="flex min-w-0 flex-col gap-4 px-6 py-4 max-md:px-4">
        {agent.isPending ? (
          <Loading label="Loading the agent" rows={3} />
        ) : agent.isError ? (
          <LoadError what="The agent" error={agent.error} />
        ) : a ? (
          <AgFacts api={api} company={company} agent={a} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * WF-POLISH (Andrei, 2026-10-27: the AG page "is mostly empty space"): full width, what it does
 * first (role, goal, backstory, responsibilities, standing orders: its operating profile or the
 * add dialog; the pencil edits them for those who may change it), then whom it reports to and
 * which DE uses it, its recent work (only its last reported activity: work inside its DE's
 * container is not reported to CompanyOS yet, and the line says so), and the runtime as one line
 * with the details behind an (i).
 */
function AgFacts({
  api,
  company,
  agent: a,
}: {
  api: Api;
  company: CompanyAccess;
  agent: WorkforceAgentRow;
}) {
  const [editing, setEditing] = useState(false);
  const dm = useOpenDm(api, company);
  const chat = useFeature("chat");
  const { me } = useSession();
  const mine = myMembershipId(me, company);
  const canMessage = chat.enabled && !chat.readOnly && mine !== null;
  const activity = activityLabel(a);
  const r = a.runtime;
  const de = a.reports_to.kind === "digital_employee";
  const runtimeText = r
    ? [
        "Hermes",
        r.container,
        r.profile,
        r.clawpanel_instance ? `ClawPanel #${r.clawpanel_instance}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;
  const deLink = de ? (
    <Link
      to="/c/$slug/agents/de/$id"
      params={{ slug: company.slug, id: a.reports_to.membership_id }}
      search={keepFocus}
      className="truncate text-primary-text underline-offset-4 hover:underline"
      data-slot="ag-used-by-link"
    >
      {workerName(a.reports_to.name)}
    </Link>
  ) : null;
  const items: { key: string; label: string; value: ReactNode }[] = [
    {
      key: "reports-to",
      label: "Reports to",
      value: (
        <span className="inline-flex min-w-0 items-center gap-1.5" data-slot="ag-reports-to">
          <WorkerBadge kind={de ? "de" : "person"} size="sm" />
          {de ? (
            <Link
              to="/c/$slug/agents/de/$id"
              params={{ slug: company.slug, id: a.reports_to.membership_id }}
              search={keepFocus}
              className="truncate text-primary-text underline-offset-4 hover:underline"
              data-slot="ag-reports-to-link"
            >
              {workerName(a.reports_to.name)}
            </Link>
          ) : (
            <>
              <span className="truncate">{a.reports_to.name}</span>
              {/* Only where a DM can open: Chat on and writable, a member caller, not oneself. */}
              {canMessage && a.reports_to.membership_id !== mine ? (
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={dm.isPending}
                  onClick={() => dm.mutate(a.reports_to.membership_id)}
                >
                  <MessageSquareIcon data-icon="inline-start" />
                  Message
                </Button>
              ) : null}
            </>
          )}
        </span>
      ),
    },
    {
      key: "used-by",
      label: "Used by",
      value: deLink ?? (
        <span className="text-muted-foreground" data-slot="ag-used-by-none">
          No DE: it works for a person
        </span>
      ),
    },
    { key: "team", label: "Team", value: a.team?.name ?? "No team" },
    {
      key: "status",
      label: "Status",
      value: (
        <span className="inline-flex items-center gap-1.5">
          {AG_STATUS[a.status].label}
          <InfoTip label="About the agent's status">{AG_RECORD_HINT}</InfoTip>
        </span>
      ),
    },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-8" data-slot="ag-page">
      {editing ? (
        <AgProfileDialog api={api} company={company} agent={a} onClose={() => setEditing(false)} />
      ) : null}
      <div className="flex min-w-0 flex-col gap-6" data-slot="ag-what-it-does">
        <RuntimeSyncChip api={api} co={company.id} subject={{ kind: "ag", id: a.id }} />
        <ProfileFields
          role={a.role}
          goal={a.goal}
          backstory={a.backstory}
          onEdit={a.can_edit ? () => setEditing(true) : undefined}
          editLabel={`Edit what ${workerName(a.name)} does`}
        />
        <div className="grid min-w-0 gap-6 lg:grid-cols-2">
          <section className="flex min-w-0 flex-col gap-2" aria-label="Responsibilities">
            <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              Responsibilities
            </h3>
            {a.responsibilities.length ? (
              <ul
                className="flex list-disc flex-col gap-1 pl-4 text-sm"
                data-slot="ag-responsibilities"
              >
                {a.responsibilities.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None written yet.</p>
            )}
          </section>
          {/* HELPERS: its skills, as the Helpers card shows them. */}
          {a.skills.length ? (
            <section className="flex min-w-0 flex-col gap-2" aria-label="Skills">
              <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Skills
              </h3>
              <SkillChips skills={a.skills} company={company} />
            </section>
          ) : null}
          <section className="flex min-w-0 flex-col gap-2" aria-label="Standing orders">
            <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              Standing orders
            </h3>
            {a.standing_orders ? (
              <Expandable text={a.standing_orders} slot="ag-standing-orders" />
            ) : (
              <p className="text-sm text-muted-foreground">None written yet.</p>
            )}
          </section>
        </div>
      </div>
      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <dl
          className="grid min-w-0 grid-cols-[max-content_1fr] content-start gap-x-6 gap-y-2 text-sm max-sm:grid-cols-1"
          data-slot="ag-facts"
        >
          {items.map((i) => (
            <div key={i.key} className="contents" data-fact={i.key}>
              <dt className="text-muted-foreground">{i.label}</dt>
              <dd className="min-w-0 text-foreground max-sm:mb-1">{i.value}</dd>
            </div>
          ))}
        </dl>
        <section className="flex min-w-0 flex-col gap-2" aria-label="Recent work">
          <h3 className="flex items-center gap-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Recent work
            <InfoTip label="About recent work">
              Work inside its DE's container isn't reported yet. This is the last activity recorded
              for it.
            </InfoTip>
          </h3>
          {activity ? (
            <p className="text-sm" data-slot="ag-activity">
              {activity.text}
              {activity.full ? (
                <>
                  {" · "}
                  <time dateTime={a.last_activity_at ?? undefined} data-slot="ag-activity-at">
                    {activity.full}
                  </time>
                </>
              ) : null}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground" data-slot="ag-activity">
              Work inside its DE's container isn't reported yet.
            </p>
          )}
        </section>
      </div>
      <RuntimeLine
        summary={runtimeText ? "Hermes" : "Not recorded"}
        items={[
          {
            label: "Runs on",
            value: runtimeText ? <span data-slot="ag-runtime">{runtimeText}</span> : "Not recorded",
          },
          { label: "Slug", value: <code className="font-mono text-xs">{a.slug}</code> },
          { label: "Registered", value: formatDate(a.created_at) },
          { label: "Changed", value: formatDateTime(a.updated_at) },
        ]}
      />
    </div>
  );
}

/** Role, goal, backstory, responsibilities and standing orders of an agent (who may change it). */
function AgProfileDialog({
  api,
  company,
  agent: a,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  agent: WorkforceAgentRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (body: ProfileDraft) =>
      api.client.workforceAgents.update({ params: { co: company.id, id: a.id }, body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.workforceAgents(company.id) });
      toast.add({ title: "Saved", type: "success" });
      onClose();
    },
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  return (
    <ProfileDialog
      title={`What ${workerName(a.name)} does`}
      initial={a}
      withStandingOrders
      withResponsibilities
      withSkills
      pending={save.isPending}
      onSave={(d) => save.mutate(d)}
      onClose={onClose}
    />
  );
}
